import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import PublicLayout from '../components/PublicLayout';
import { Download, BarChart2, ShieldCheck, User, Award, Shield, ArrowRight, Check, Bot, Database } from 'lucide-react';

// ── Inline leaderboard card (design-specific layout) ──────────────────────────
function LeaderboardCard({ analyst, rank, onViewProfile }) {
  const avgReturn = parseFloat(analyst.overall_return_pct ?? 0);
  const returnColor = avgReturn === 0 ? 'text-gray-500' : avgReturn > 0 ? 'text-emerald-600' : 'text-red-500';

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-black/5 transition-all border border-[#e2e2eb]/30 flex flex-col">
      {/* Header: avatar + rank badge */}
      <div className="flex justify-between items-start mb-6">
        <div className="w-12 h-12 rounded-full bg-[#e7e7f0] flex items-center justify-center overflow-hidden flex-shrink-0">
          {analyst.profile_image_url
            ? <img src={analyst.profile_image_url} alt={analyst.name} className="w-full h-full object-cover" />
            : <span className="font-bold text-[#424753] text-lg">{analyst.name?.[0]}</span>}
        </div>
        <span className="text-xs font-bold text-[#006a40] px-2 py-1 bg-[#78fbb3] rounded font-mono">
          #{rank} RANK
        </span>
      </div>

      {/* Name — 2 lines reserved */}
      <h4 className="font-bold text-[#191b22] text-lg leading-tight mb-4 min-h-[3rem]">
        {analyst.name}
      </h4>

      <div className="space-y-3 mb-6">
        <div className="flex justify-between items-center text-[10px] text-[#424753]/60 uppercase tracking-tighter">
          <span>{analyst.predictions_count ?? 0} Predictions</span>
          <span className={`font-semibold ${returnColor}`}>
            {avgReturn === 0 ? 'N/A' : `${avgReturn >= 0 ? '+' : ''}${avgReturn.toFixed(1)}%`} Avg Return
          </span>
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={onViewProfile}
        className="w-full py-2.5 bg-[#4F8EF7] text-white rounded-lg font-bold text-sm hover:brightness-110 active:scale-95 transition-all text-center mt-auto"
      >
        View Profile
      </button>
    </div>
  );
}

// ── Pricing section with Consumer / Institutional toggle ───────────────────────
function LandingPricing() {
  const [tab, setTab] = useState('consumer');
  const [consumerPlans, setConsumerPlans] = useState([]);
  const [institutionalPlans, setInstitutionalPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE_URL}/api/payments/subscription/plans/?tenant=analyst_consumer`),
      axios.get(`${API_BASE_URL}/api/payments/subscription/plans/?tenant=analyst_institutional`),
    ])
      .then(([cRes, iRes]) => {
        setConsumerPlans(cRes.data);
        setInstitutionalPlans(iRes.data);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const monthly = [...consumerPlans]
    .filter((p) => p.billing_interval === 'monthly' && p.is_active)
    .sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

  const BADGE = ['Browse', 'Track', 'Decode'];
  const BADGE_STYLE = [
    'bg-[#78fbb3] text-[#002110]',
    'bg-[#d7e2ff] text-[#004492]',
    'bg-[#c7e9e5] text-[#2d4c49]',
  ];
  const BTN_LABEL = ['Start Browsing', 'Go Professional', 'Join Elite'];
  const BTN_STYLE = [
    'bg-[#e7e7f0] text-[#191b22] hover:bg-[#e2e2eb]',
    'bg-[#4F8EF7] text-white shadow-xl shadow-[#4F8EF7]/20 hover:brightness-110',
    'border-2 border-[#c2c6d5]/30 text-[#191b22] hover:bg-[#f3f3fc]',
  ];

  return (
    <section className="space-y-12 py-16 scroll-mt-24" id="pricing">
      <div className="text-center max-w-3xl mx-auto space-y-8">
        <h3 className="text-5xl font-extrabold text-[#191b22] tracking-tighter">Choose Your Edge</h3>
        <p className="text-[#424753] text-lg">
          Bespoke intelligence for retail conviction and institutional compliance.
        </p>
        {/* Toggle */}
        <div className="inline-flex items-center p-1.5 bg-[#f0f0f9] rounded-full border border-[#c2c6d5]/30">
          <button
            onClick={() => setTab('consumer')}
            className={`px-10 py-3 rounded-full text-sm font-semibold transition-all ${tab === 'consumer' ? 'bg-white shadow-md text-[#191b22] font-bold' : 'text-[#424753]'
              }`}
          >
            Consumer
          </button>
          <button
            onClick={() => setTab('institutional')}
            className={`px-10 py-3 rounded-full text-sm font-semibold transition-all ${tab === 'institutional' ? 'bg-white shadow-md text-[#191b22] font-bold' : 'text-[#424753]'
              }`}
          >
            Institutional
          </button>
        </div>
      </div>

      <div className="max-w-8xl mx-auto pt-4">
        {loading ? (
          <div className="grid grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white rounded-3xl h-80 animate-pulse border border-gray-100" />
            ))}
          </div>
        ) : tab === 'consumer' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {monthly.map((plan, idx) => {
              const price = parseFloat(plan.price);
              const features = plan.description
                ? plan.description.split('\n').filter(Boolean).slice(0, 5)
                : [];
              const isHighlight = plan.highlight || idx === 1;

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-3xl p-8 flex flex-col shadow-sm transition-all relative ${isHighlight
                      ? 'border-2 border-[#4F8EF7] shadow-2xl shadow-[#4F8EF7]/5 hover:scale-[1.02]'
                      : 'border border-[#c2c6d5]/30 hover:shadow-md'
                    }`}
                >
                  {isHighlight && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#4F8EF7] text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-[#4F8EF7]/30 whitespace-nowrap z-20">
                      Most Popular
                    </div>
                  )}
                  <div className="mb-8">
                    <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 ${BADGE_STYLE[idx] ?? BADGE_STYLE[2]}`}>
                      {BADGE[idx] ?? plan.name}
                    </span>
                    <h5 className={`text-lg font-bold mb-1 ${isHighlight ? 'text-[#4F8EF7]' : 'text-[#191b22]'}`}>
                      {plan.name}
                    </h5>
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-black text-[#191b22]">${price === 0 ? '0' : price}</span>
                      <span className="text-[#424753]/60 text-sm font-medium">/mo</span>
                    </div>
                  </div>
                  <div className="flex-1 space-y-3 mb-8">
                    {features.map((f, i) => (
                      <div key={i} className="flex items-start gap-3 text-sm text-[#424753]">
                        <Check size={16} className="text-[#4F8EF7] flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => { window.location.href = '/login'; }}
                    className={`mt-auto w-full py-4 rounded-xl font-bold text-sm active:scale-[0.98] transition-all ${BTN_STYLE[idx] ?? BTN_STYLE[2]}`}
                  >
                    {BTN_LABEL[idx] ?? 'Get Started'}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {institutionalPlans.map((plan, idx) => {
              const INST_ICONS = [
                <div key="shield" className="w-14 h-14 bg-[#c7e9e5]/40 rounded-2xl flex items-center justify-center mb-8"><Shield size={28} className="text-[#456461]" /></div>,
                <div key="bot" className="w-14 h-14 bg-[#d7e2ff]/40 rounded-2xl flex items-center justify-center mb-8"><Bot size={28} className="text-[#4F8EF7]" /></div>,
                <div key="db" className="w-14 h-14 bg-[#e7e7f0]/40 rounded-2xl flex items-center justify-center mb-8"><Database size={28} className="text-[#424753]" /></div>,
              ];
              const INST_BTN = [
                { label: 'Request Demo', cls: 'bg-white text-[#191b22] border border-[#c2c6d5]/30 hover:bg-[#f3f3fc]' },
                { label: 'Get API Key', cls: 'bg-white text-[#191b22] border border-[#c2c6d5]/30 hover:bg-[#f3f3fc]' },
                { label: 'Contact Sales', cls: 'bg-[#191b22] text-white hover:bg-[#2e3037]' },
              ];
              const INST_PERIOD = ['/seat', '/mo', '/yr'];
              const btn = INST_BTN[idx] ?? INST_BTN[2];
              return (
                <div
                  key={plan.id}
                  className="bg-white/60 backdrop-blur-sm rounded-3xl p-10 border border-[#c2c6d5]/20 flex flex-col shadow-sm hover:shadow-md transition-all"
                >
                  {INST_ICONS[idx]}
                  <h6 className="text-xl font-bold text-[#191b22] mb-2">{plan.name}</h6>
                  <p className="text-sm text-[#424753] mb-8 font-medium">{plan.subtitle}</p>
                  <div className="flex items-baseline gap-1 mt-auto mb-8">
                    <span className="text-4xl font-black text-[#191b22]">{plan.price}</span>
                    <span className="text-[#424753]/60 text-[10px] font-black uppercase tracking-widest">
                      {INST_PERIOD[idx] ?? '/mo'}
                    </span>
                  </div>
                  <button
                    onClick={() => { window.location.href = '/login'; }}
                    className={`w-full py-4 rounded-xl font-bold text-sm transition-colors ${btn.cls}`}
                  >
                    {btn.label}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AnalystLandingPage() {
  const navigate = useNavigate();
  const [top5, setTop5] = useState([]);
  const [loading, setLoading] = useState(true);

  function handleViewProfile(slug) {
    navigate(`/analysts/${slug}`);
  }

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/research/analysts/?ordering=leaderboard_score&limit=7`)
      .then((res) => setTop5(res.data))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <div className="px-8 py-8 space-y-16">

        {/* ── Hero ── */}
        <section className="relative rounded-3xl overflow-hidden min-h-[400px] flex items-center px-16 bg-[#f3f3fc]">
          <div className="relative z-10 max-w-2xl space-y-6 py-20">
            <span className="inline-block py-1 px-3 bg-[#c7e9e5] text-[#4b6a67] rounded-full text-xs font-semibold tracking-wider uppercase">
              The Credit Score for Financial Influencers
            </span>
            <h2 className="text-6xl font-extrabold text-[#191b22] tracking-tight leading-[1.1]">
              Who's Actually <br />
              <span className="text-[#4F8EF7]">Right?</span>
            </h2>
            <p className="text-xl text-[#424753] leading-relaxed max-w-xl">
              Unmasking market consensus with cold, hard data. We track every analyst, every
              prediction, and every outcome to find the signal in the noise.
            </p>
            <div className="flex gap-4 pt-4">
              <Link
                to="/leaderboard"
                className="px-8 py-4 bg-gradient-to-br from-[#4F8EF7] to-[#2c72d9] text-white rounded-xl font-bold shadow-xl shadow-[#4F8EF7]/20 active:scale-95 transition-transform"
              >
                Explore Leaderboards
              </Link>
              <a
                href="#how-it-works"
                className="px-8 py-4 bg-[#e2e2eb] text-[#191b22] rounded-xl font-bold active:scale-95 transition-transform"
              >
                View Methods
              </a>
            </div>
          </div>
          {/* Right image — faint architectural photo */}
          <div className="absolute right-0 top-0 bottom-0 w-1/2 overflow-hidden pointer-events-none">
            <img
              alt=""
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuAl6hPgmPEfI7lmH-RbdQGj7NtY_K2FNynfd7XtPXwOu-aHcg4d8R7XSOxQPmfV1Nl0UgsFaBBHlW--Lq282Jnkd8HhOJE_zjarAS1Bp0QdtYVuIT6zJakPkXbm9XdEEWshOgd2ikxgxt6TSkF29vhNyLrMLompny5jN_Kk5DRBZaSpJCgC6bmtRbbTjUiFf_FYiwBn-EjwhNEtIZ6v-CS2FBNZOr0DU9Kb7NHDd_--hClF5R_z8fedQ07HZNZlvF6CIxyqe7QFEPyE"
              className="w-full h-full object-cover mix-blend-multiply opacity-20 grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-[#f3f3fc] via-transparent to-transparent" />
          </div>
        </section>

        {/* ── Top Accuracy Leaderboard ── */}
        <section className="space-y-8">
          <div className="flex justify-between items-end">
            <div className="space-y-2">
              <h3 className="text-3xl font-bold text-[#191b22]">Top Accuracy Leaderboard</h3>
              <p className="text-[#424753]">Real-time performance tracking of the world's most followed analysts.</p>
            </div>
            <Link
              to="/leaderboard"
              className="text-[#4F8EF7] font-bold flex items-center gap-2 hover:underline"
            >
              View All Analysts <ArrowRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="flex gap-6 overflow-x-auto pb-2 scrollbar-hide">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse h-64 flex-shrink-0 w-64" />
              ))}
            </div>
          ) : top5.length === 0 ? (
            <p className="text-[#424753] text-sm">No analysts available yet.</p>
          ) : (
            <div className="flex gap-6 overflow-x-auto pb-2 scrollbar-hide">
              {top5.map((analyst, idx) => (
                <div key={analyst.id} className="flex-shrink-0 w-64">
                  <LeaderboardCard analyst={analyst} rank={idx + 1} onViewProfile={() => handleViewProfile(analyst.slug)} />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Three Pillars of Integrity ── */}
        <section id="how-it-works" className="py-24 bg-[#f3f3fc] rounded-[3rem]">
          <div className="max-w-4xl mx-auto px-12">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl font-bold tracking-tight text-[#191b22]">The Three Pillars of Integrity</h2>
              <p className="text-[#424753] max-w-xl mx-auto">
                How we transform raw social media predictions into verified accountability data.
              </p>
            </div>
            <div className="relative space-y-24 before:absolute before:left-8 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#c2c6d5]/30">
              {[
                {
                  icon: Download,
                  title: '01. Extract',
                  desc: "Our NLP engine monitors Twitter/X, YouTube, TikTok, and Instagram — extracting explicit stock predictions from finfluencer posts. Ticker, direction, price target, and timeframe — captured automatically. The hard part isn't scraping — it's classifying actual predictions from sarcasm, hedging, and vague commentary.",
                },
                {
                  icon: BarChart2,
                  title: '02. Score',
                  desc: "Every prediction is resolved against real market data when its timeframe expires. Win, Loss, or Expired — no human judgment. From this, we compute accuracy rates, sector performance, and our proprietary First Call vs. Follower Return metric — revealing whether an analyst discovers early or just echoes moves already underway.",
                },
                {
                  icon: ShieldCheck,
                  title: '03. Validate',
                  desc: "Our AI validation layer cross-references each prediction against fundamentals and the analyst's own track record. Is the call aligned with earnings data? Is the pattern consistent — or does it look like promotion? Transparent scoring, open methodology, and a database that compounds daily.",
                },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="relative flex gap-12 group">
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center z-10 border border-[#c2c6d5]/10 group-hover:bg-[#4F8EF7] transition-colors flex-shrink-0">
                    <Icon size={22} className="text-[#4F8EF7] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1 pt-2">
                    <h4 className="text-2xl font-bold mb-3 text-[#191b22]">{title}</h4>
                    <p className="text-[#424753] leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Architected for Every Desk ── */}
        <section className="space-y-12 py-12">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <h3 className="text-4xl font-extrabold text-[#191b22] tracking-tight">Architected for Every Desk</h3>
            <p className="text-[#424753]">
              Whether you're checking a finfluencer before following a trade or monitoring compliance risk across your firm, TraderBro is the accountability layer that didn't exist until now.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-12">
            {[
              {
                icon: User,
                title: 'Retail Investors',
                desc: '48% of Gen Z gets investment advice from social media — with no way to verify accuracy. Search any finfluencer, see their verified score, and browse their full prediction history before you follow.',
                bg: 'bg-[#d7e2ff]',
                iconColor: 'text-[#4F8EF7]',
              },
              {
                icon: Award,
                title: 'Finfluencers',
                desc: "If you're actually good, prove it. Claim Your Call to get tracked and scored. Share your verified scorecard — credibility that follower count can't buy. It's free, always.",
                bg: 'bg-[#c7e9e5]',
                iconColor: 'text-[#456461]',
              },
              {
                icon: Shield,
                title: 'Compliance Teams',
                desc: 'FINRA has fined broker-dealers $1.2M+ for inadequate finfluencer monitoring in the past year. TraderBro provides the risk data your team needs — accuracy trends, prediction patterns, and conduct signals across 500+ tracked influencers.',
                bg: 'bg-[#e2e2eb]',
                iconColor: 'text-[#191b22]',
              },
            ].map(({ icon: Icon, title, desc, bg, iconColor }) => (
              <div key={title} className="space-y-6 text-center">
                <div className={`w-16 h-16 ${bg} rounded-2xl flex items-center justify-center mx-auto shadow-inner`}>
                  <Icon size={28} className={iconColor} />
                </div>
                <h4 className="text-xl font-bold text-[#191b22]">{title}</h4>
                <p className="text-[#424753] text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Choose Your Edge (Pricing) ── */}
        <LandingPricing />

        {/* ── Newsletter ── */}
        <section className="py-24 max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h3 className="text-3xl font-bold text-[#191b22] tracking-tight">Weekly Finfluencer Accuracy Report</h3>
            <p className="text-[#424753] text-lg max-w-2xl mx-auto leading-relaxed">
              Get the top calls, worst misses, and accuracy trends delivered every Friday.
            </p>
          </div>
          <form
            className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="email"
              required
              placeholder="Enter your work email"
              className="flex-1 px-6 py-4 bg-white border border-[#c2c6d5] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4F8EF7]/20 focus:border-[#4F8EF7] transition-all text-[#191b22]"
            />
            <button
              type="submit"
              className="px-8 py-4 bg-[#4F8EF7] text-white rounded-xl font-bold shadow-lg shadow-[#4F8EF7]/20 hover:brightness-110 transition-all active:scale-95"
            >
              Subscribe
            </button>
          </form>
        </section>

        {/* ── Footer ── */}
        <footer className="w-full py-12 px-8 flex flex-col items-center gap-6 text-center bg-[#f3f3fc] -mx-8 rounded-t-[3rem]">
          <div className="font-bold text-[#191b22] text-2xl">TraderBro</div>
          <div className="flex gap-8">
            <a href="#" className="text-xs uppercase tracking-widest text-[#424753] opacity-60 hover:opacity-100 transition-opacity">Terms of Service</a>
            <a href="#" className="text-xs uppercase tracking-widest text-[#424753] opacity-60 hover:opacity-100 transition-opacity">Privacy Policy</a>
            <a href="#" className="text-xs uppercase tracking-widest text-[#424753] opacity-60 hover:opacity-100 transition-opacity">Contact</a>
          </div>
          <p className="text-xs uppercase tracking-widest text-[#424753] opacity-40">
            © 2025 TraderBro Atelier. All rights reserved.
          </p>
        </footer>

      </div>
    </PublicLayout>
  );
}
